const DEVICE_ID_KEY = 'gympro_device_id';

const createFallbackId = () => {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Stable per-browser device identifier. Stored in localStorage and used
 * by the self check-in RPC to bind a client to a single device.
 */
export const getOrCreateDeviceId = (): string => {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;

    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : createFallbackId();

    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return createFallbackId();
  }
};

export const getDeviceLabel = (): string => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  let platform = 'Dispositivo';
  if (/android/i.test(ua)) platform = 'Android';
  else if (/iphone|ipad|ipod/i.test(ua)) platform = 'iOS';
  else if (/windows/i.test(ua)) platform = 'Windows';
  else if (/mac os/i.test(ua)) platform = 'Mac';
  else if (/linux/i.test(ua)) platform = 'Linux';

  let browser = '';
  if (/edg\//i.test(ua)) browser = 'Edge';
  else if (/chrome|crios/i.test(ua)) browser = 'Chrome';
  else if (/firefox|fxios/i.test(ua)) browser = 'Firefox';
  else if (/safari/i.test(ua)) browser = 'Safari';

  return browser ? `${platform} · ${browser}` : platform;
};

export type GeolocationResult = {
  latitude: number;
  longitude: number;
  accuracy: number;
};

export const getCurrentCoordinates = (timeoutMs = 12000): Promise<GeolocationResult> => {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('NO_GEOLOCATION'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
    );
  });
};
